import React, { Component } from 'react';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";


export default class DiagnosisPlot extends Component {
  constructor(props) {
    super(props);

    this.state = {
      photoIndex: 0,
      isOpen: false,
      loadedPlots: [], // Plots that have been successfully loaded
      loadingPlot: false,
    };
  }

  // Generate the plot info for a given index
  // Index 0 = current, 1 = 15th of current month, 2 = 1st of current month, etc.
  getPlotInfoByIndex(index) {
    const { location, panID } = this.props;
    const baseUrl = 'https://blickm.pandonia-global-network.org/plots';

    if (index === 0) {
      // Current plot (no date)
      return {
        url: `${baseUrl}/${panID}/P${panID}.png`,
        label: 'Current',
        date: null,
        index: 0
      };
    }

    // Calculate which month and which day (15th or 1st)
    // index 1 = 15th of current month (month offset 0)
    // index 2 = 1st of current month (month offset 0)
    // index 3 = 15th of previous month (month offset 1)
    // index 4 = 1st of previous month (month offset 1)
    const dayIndex = (index - 1) % 2; // 0 = 15th, 1 = 1st
    const monthOffset = Math.floor((index - 1) / 2);

    const today = new Date();
    const targetDate = new Date(today.getFullYear(), today.getMonth(), 1);
    targetDate.setMonth(targetDate.getMonth() - monthOffset);

    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, '0');
    const day = dayIndex === 0 ? '15' : '01';
    const dateStr = `${year}${month}${day}`;

    return {
      url: `${baseUrl}/${panID}/P${panID}_${dateStr}_${location}.png`,
      label: `${year}-${month}-${day}`,
      date: dateStr,
      index
    };
  }

  // Check if a plot exists and load it
  async loadPlot(index) {
    const { loadedPlots } = this.state;

    // Check if already loaded
    if (loadedPlots.find(p => p.index === index)) {
      return true;
    }

    this.setState({ loadingPlot: true });

    const plotInfo = this.getPlotInfoByIndex(index);

    // Use Image preload to check if the plot exists (avoids CORS issues)
    return new Promise((resolve) => {
      const img = new Image();

      img.onload = () => {
        this.setState(prevState => ({
          loadedPlots: [...prevState.loadedPlots, plotInfo].sort((a, b) => a.index - b.index),
          loadingPlot: false
        }));
        resolve(true);
      };

      img.onerror = () => {
        this.setState({ loadingPlot: false });
        resolve(false);
      };

      img.src = plotInfo.url;
    });
  }

  componentDidMount() {
    // Load the current plot (index 0) immediately
    this.loadPlot(0);
  }

  handlePrevious = async () => {
    const { photoIndex, loadedPlots } = this.state;

    // Try to load the next plot in history (higher index)
    let nextIndex = photoIndex + 1;
    const maxAttempts = 10; // Try up to 10 indices to find an existing plot

    for (let attempts = 0; attempts < maxAttempts; attempts++) {
      const success = await this.loadPlot(nextIndex);
      if (success) {
        // Find the position of this plot in loadedPlots
        const loadedIndex = loadedPlots.findIndex(p => p.index === nextIndex);
        if (loadedIndex !== -1 || this.state.loadedPlots.find(p => p.index === nextIndex)) {
          this.setState({ photoIndex: nextIndex });
        }
        break;
      }
      nextIndex++; // Try next index if current one doesn't exist
    }
  };

  handleNext = () => {
    const { photoIndex } = this.state;
    if (photoIndex > 0) {
      this.setState({ photoIndex: photoIndex - 1 });
    }
  };

  render() {
    const { photoIndex, isOpen, loadedPlots, loadingPlot } = this.state;
    const { location, panID } = this.props;

    // Get current plot by index
    const currentPlot = loadedPlots.find(p => p.index === photoIndex) ||
      { url: `https://blickm.pandonia-global-network.org/plots/${panID}/P${panID}.png`, label: 'Current' };

    const canGoPrevious = !loadingPlot; // Can always try to go to previous (older plots)
    const canGoNext = photoIndex > 0;

    return (
      <div>
        <Button variant="outlined" onClick={() => this.setState({ isOpen: true, photoIndex: 0 })}>
          Diagnosis Plot
        </Button>

        {isOpen && (
          <Lightbox
            mainSrc={currentPlot.url}
            onCloseRequest={() => this.setState({ isOpen: false })}
            onMovePrevRequest={this.handlePrevious}
            onMoveNextRequest={this.handleNext}
            imageTitle={currentPlot.label}
            imageCaption={loadingPlot ? 'Loading plot...' : `Plot index: ${photoIndex}`}
            enableZoom={true}
            toolbarButtons={[
              <div key="navigation" style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'white' }}>
                <IconButton
                  onClick={this.handlePrevious}
                  disabled={!canGoPrevious}
                  style={{ color: canGoPrevious ? 'white' : 'gray' }}
                  title="Previous (older) plot"
                >
                  <ArrowBackIcon />
                </IconButton>
                <span>{currentPlot.label}</span>
                <IconButton
                  onClick={this.handleNext}
                  disabled={!canGoNext}
                  style={{ color: canGoNext ? 'white' : 'gray' }}
                  title="Next (newer) plot"
                >
                  <ArrowForwardIcon />
                </IconButton>
              </div>
            ]}
          />
        )}
      </div>
    );
  }
}
